/**
 * Agenti d'inventario: macchine che parlano di sé.
 *
 * ## Perché esistono
 *
 * Una sonda non raggiunge tutto. Un server in una nuvola altrui, un host dietro
 * un firewall che non si apre, una rete che non si attraversa: per quelli
 * l'inventario può venire solo da dentro, da un processo che raccoglie e
 * consegna.
 *
 * ## La cosa che questa schermata deve fare bene
 *
 * Il segreto compare **una volta sola**. Nel registro c'è la sua impronta, non
 * il valore: chi legge il database non può impersonare un agente, ed è il
 * motivo per cui questa pagina insiste tanto sul copiarlo adesso. Chi lo perde
 * non lo rilegge — ne genera un altro, che è l'operazione giusta.
 */
import { useState } from 'react'

import { CellaTag, useTagDi } from '../components/Tag'

import { api } from '../api/client'
import { usePermessiModuli } from '../auth/permessi'
import { PercorsiSoftware } from '../components/PercorsiSoftware'
import { LogDispositivo } from '../components/LogDispositivo'
import { Finestra } from '../components/ModificheGitlab'
import { DataTable, type Column } from '../components/DataTable'
import type { InventoryAgent } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  PageSection,
  SectionedPage,
  Spinner,
  timeAgo,
} from '../components/common'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { VarianteSenzaCurl } from '../components/VarianteSenzaCurl'
import { PacchettoDistribuzione } from '../components/PacchettoDistribuzione'
import { SbomCenso } from '../components/SbomCenso'

/** Come si legge lo stato di un agente, e con che tono. */
const STATO: Record<string, { label: () => string; tono: string; spiega: () => string }> = {
  active: {
    label: () => t('Attivo'),
    tono: 'ok',
    spiega: () => t('Ha consegnato, e l’ultima consegna è andata a buon fine.'),
  },
  announced: {
    label: () => t('Installato'),
    tono: 'info',
    spiega: () => t('La macchina si è annunciata: l’agente è installato e il primo inventario sta arrivando.'),
  },
  key: {
    label: () => t('Chiave d’installazione'),
    tono: 'info',
    spiega: () => t('Una configurazione da installare su più macchine: ognuna, al primo avvio, si registra come agente a sé con il proprio nome.'),
  },
  never_delivered: {
    label: () => t('Mai consegnato'),
    tono: 'warn',
    spiega: () =>
      t('Registrato ma non ha ancora parlato: la configurazione non è ancora sulla macchina, o l’esecuzione periodica non è stata impostata.'),
  },
  failing: {
    label: () => t('Consegna rifiutata'),
    tono: 'error',
    spiega: () =>
      t('Ha parlato e non è stato accettato. Il motivo è qui sotto: è un guasto diverso dal silenzio.'),
  },
  expired: {
    label: () => t('Segreto scaduto'),
    tono: 'warn',
    spiega: () => t('Il segreto non vale più. Rigeneralo e rimetti la configurazione sulla macchina.'),
  },
  revoked: {
    label: () => t('Revocato'),
    tono: 'muted',
    spiega: () => t('Qualcuno gli ha tolto il permesso di consegnare.'),
  },
}

/** Le features dell'agente (flag nel config); un agente nasce senza. */
const FEATURES: { chiave: 'sbom' | 'runtime' | 'app_observe' | 'dhcp_listen'; label: () => string }[] = [
  { chiave: 'sbom', label: () => 'SBOM' },
  { chiave: 'runtime', label: () => t('Runtime') },
  { chiave: 'app_observe', label: () => 'eBPF' },
  { chiave: 'dhcp_listen', label: () => t('Ascolto DHCP') },
]

function featureAttive(a: InventoryAgent): string[] {
  const c = (a.config ?? {}) as Record<string, unknown>
  return FEATURES.filter((f) => c[f.chiave] === true).map((f) => f.label())
}

function Stat({ label, value, tono }: { label: string; value: number; tono?: string }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 600 }} className={tono ? `stat--${tono}` : undefined}>{value}</div>
      <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  )
}

export default function InventoryAgents() {
  const agenti = useAsync(() => api.inventoryAgents(), [])
  const [creando, setCreando] = useState(false)
  const [config, setConfig] = useState<InventoryAgent | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())
  const [aperte, setAperte] = useState<Set<string>>(new Set())
  const permessi = usePermessiModuli()
  const scrive = permessi.scrive('inventory_agents')
  const tag = useTagDi('inventory_agent')
  const dati = agenti.data ?? []

  function ricarica() {
    setSelezionati(new Set())
    agenti.reload()
  }
  function commutaImpostazioni(id: string) {
    setAperte((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  const perStato = (s: string) => dati.filter((a) => a.state === s).length
  const conFeature = (k: string) => dati.filter((a) => ((a.config ?? {}) as Record<string, unknown>)[k] === true).length

  const colonne: Column<InventoryAgent>[] = [
    {
      key: 'name',
      header: t('Nome'),
      value: (a) => a.name || a.hostname || '',
      render: (a) => (
        <>
          {a.name || a.hostname || <span className="text-muted">{t('in attesa dell’installazione')}</span>}
          {a.is_key && (<><br /><small className="text-muted">{t('chiave d’installazione · {n} macchine registrate', { n: a.installs ?? 0 })}</small></>)}
          {a.hostname && a.hostname !== a.name && (<><br /><small className="text-muted">{t('macchina')} <span className="mono">{a.hostname}</span></small></>)}
          {a.key_name && (<><br /><small className="text-muted">{t('dalla chiave «{nome}»', { nome: a.key_name })}</small></>)}
          {a.description && (<><br /><span className="text-muted">{a.description}</span></>)}
        </>
      ),
    },
    ...(tag.vede
      ? [{
          key: 'tag', header: t('Tag'), sort: false as const, filter: false as const,
          value: (a: InventoryAgent) => tag.testo(a.id),
          render: (a: InventoryAgent) => (
            <CellaTag tipo="inventory_agent" id={a.id} tags={tag.di(a.id)} scrive={tag.scrive} onChanged={() => void tag.reload()} />
          ),
        } as Column<InventoryAgent>]
      : []),
    // The installation key the agent enrolled with («chiave multi agente»):
    // one column, filterable, so the machines of one key stand together.
    {
      key: 'chiave', header: t('Chiave multi agente'), filter: 'select',
      value: (a) => (a.is_key ? t('è una chiave') : a.key_name ?? ''),
      render: (a) => (a.is_key ? <span className="text-muted">{t('è una chiave')}</span> : a.key_name ? a.key_name : <span className="text-muted">—</span>),
    },
    { key: 'os', header: 'OS', value: (a) => a.os_name || a.platform || '' },
    {
      key: 'stato', header: t('Stato'), filter: 'select',
      value: (a) => STATO[a.state]?.label() ?? a.state,
      render: (a) => {
        const v = STATO[a.state]
        const s = v ? { label: v.label(), tono: v.tono, spiega: v.spiega() } : { label: a.state, tono: 'muted', spiega: '' }
        return (
          <>
            <span className={`badge badge--${s.tono}`} title={s.spiega}>{s.label}</span>
            {a.last_error && (<><br /><small className="text-muted" title={a.last_error}>{t('Ultima consegna rifiutata')}</small></>)}
          </>
        )
      },
    },
    {
      key: 'features', header: t('Features attive'),
      value: (a) => featureAttive(a).join(' ') || t('nessuna'),
      render: (a) => {
        const f = featureAttive(a)
        if (!f.length) return <span className="text-muted">{t('nessuna')}</span>
        return <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{f.map((x) => <span key={x} className="badge badge--info">{x}</span>)}</span>
      },
    },
    { key: 'contatto', header: t('Ultimo contatto'), sort: false, filter: false, render: (a) => <Contatto agente={a} /> },
    {
      key: 'consegna', header: t('Ultima consegna'), value: (a) => a.last_seen ?? '',
      render: (a) => (
        <>
          {timeAgo(a.last_seen)}
          {a.last_internal_ip && (<><br /><span className="text-muted">{t('interno')} </span><span className="mono">{a.last_internal_ip}</span></>)}
          {a.last_source_ip && (<><br /><span className="text-muted">{t('origine')} </span><span className="text-muted mono">{a.last_source_ip}</span></>)}
        </>
      ),
    },
    { key: 'deliveries', header: t('Consegne'), value: (a) => a.deliveries },
    { key: 'scadenza', header: t('Scadenza'), value: (a) => a.expires_at ?? '', render: (a) => (a.expires_at ? timeAgo(a.expires_at) : t('nessuna')) },
    {
      key: 'azioni', header: '', sort: false, filter: false,
      render: (a) => (
        <AzioniAgente agente={a} aperto={aperte.has(a.id)} onToggle={() => commutaImpostazioni(a.id)}
          onChanged={ricarica} onConfig={setConfig} onError={setError} />
      ),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t("Agenti d'inventario")}</h1>
          <p className="page__subtitle">
            {t('Per gli asset non raggiungibili da alcuna sonda, l’inventario viene acquisito localmente e inviato in modo sicuro')}
          </p>
        </div>
        <button onClick={() => setCreando(true)} disabled={creando}>{t('Nuovo agente')}</button>
      </header>

      <ErrorBanner error={error ?? agenti.error} />

      {creando && (
        <NuovoAgente onCancel={() => setCreando(false)} onDone={(agente) => { setCreando(false); setConfig(agente); agenti.reload() }} />
      )}
      {/* Il segreto compare una volta sola: in cima, non in fondo. */}
      {config && <Installazione agente={config} onClose={() => setConfig(null)} />}

      <SectionedPage
        sections={[
          { id: 'agenti', label: t('Agenti') },
          { id: 'riepilogo', label: t('Riepilogo') },
          { id: 'percorsi', label: t('Percorsi software') },
          { id: 'sbom', label: t('SBOM dell’agente') },
        ]}
      >
        <PageSection id="agenti">
          <Card>
            {agenti.loading && !agenti.data ? (
              <Spinner />
            ) : !dati.length ? (
              <EmptyState
                title={t('Nessun agente')}
                hint={t('Serve per le macchine che la sonda non riesce a interrogare: un server in cloud, un host dietro un firewall.')}
              />
            ) : (
              <>
                {selezionati.size > 0 && (
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', marginBottom: 12, borderRadius: 8, background: 'var(--surface-2, rgba(127,127,127,0.08))' }}>
                    <strong>{t('{n} agenti selezionati', { n: selezionati.size })}</strong>
                    {scrive && <AzioniMassiveAgenti ids={[...selezionati]} onFatto={ricarica} onError={setError} />}
                    <button type="button" className="link" onClick={() => setSelezionati(new Set())}>{t('Annulla la selezione')}</button>
                  </div>
                )}
                <DataTable
                  columns={colonne}
                  rows={dati}
                  rowKey={(a) => a.id}
                  initialSort={{ key: 'name' }}
                  conteggio={{ nome: t('agenti') }}
                  paginazione={{ predefinita: 25, opzioni: [25, 100] }}
                  selezione={scrive ? { chiavi: selezionati, onCambia: setSelezionati, id: (a) => a.id } : undefined}
                  expanded={(a) => aperte.has(a.id)}
                  expand={(a) => (
                    <Impostazioni agente={a} onSaved={() => { commutaImpostazioni(a.id); ricarica() }} onAnnulla={() => commutaImpostazioni(a.id)} onError={setError} />
                  )}
                />
              </>
            )}
          </Card>
        </PageSection>

        <PageSection id="riepilogo">
          <Card>
            {!dati.length ? (
              <EmptyState title={t('Nessun agente')} hint={t('Il riepilogo compare quando c’è almeno un agente.')} />
            ) : (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <Stat label={t('Agenti')} value={dati.length} />
                <Stat label={t('Attivi')} value={perStato('active')} tono="ok" />
                <Stat label={t('Installati')} value={perStato('announced')} tono="info" />
                <Stat label={t('Mai consegnato')} value={perStato('never_delivered')} tono="warn" />
                <Stat label={t('Consegna rifiutata')} value={perStato('failing')} tono="error" />
                <Stat label={t('Revocati')} value={perStato('revoked')} />
                <Stat label="SBOM" value={conFeature('sbom')} />
                <Stat label={t('Runtime')} value={conFeature('runtime')} />
                <Stat label="eBPF" value={conFeature('app_observe')} />
                <Stat label={t('Ascolto DHCP')} value={conFeature('dhcp_listen')} />
              </div>
            )}
          </Card>
        </PageSection>

        <PageSection id="percorsi">
          <PercorsiSoftware puoModificare={scrive} />
        </PageSection>
        <PageSection id="sbom">
          <SbomCenso prodotti={['agent']} titolo={t('SBOM e VEX dell’agente')} />
        </PageSection>
      </SectionedPage>
    </>
  )
}

function AzioniAgente({ agente, aperto, onToggle, onChanged, onConfig, onError }: {
  agente: InventoryAgent
  aperto: boolean
  onToggle: () => void
  onChanged: () => void
  onConfig: (a: InventoryAgent) => void
  onError: (e: unknown) => void
}) {
  const [busy, setBusy] = useState(false)
  const [log, setLog] = useState(false)
  async function azione(fn: () => Promise<unknown>) {
    setBusy(true)
    onError(null)
    try { await fn(); onChanged() } catch (err) { onError(err) } finally { setBusy(false) }
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {agente.last_keepalive_at && (
        <button type="button" className="link" disabled={busy}
          title={t('L’agente raccoglie e consegna subito, entro un minuto, senza aspettare il suo intervallo.')}
          onClick={() => azione(() => api.inventoryAgentNow(agente.id))}>{t('Inventario adesso')}</button>
      )}
      {agente.last_keepalive_at && (
        <button type="button" className="link" disabled={busy}
          title={t('La coda del registro dell’agente, letta dalla macchina al prossimo keep-alive: si legge qui e si scarica.')}
          onClick={() => setLog(true)}>{t('Log')}</button>
      )}
      {log && (
        <Finestra titolo={t('Log dell’agente «{n}»', { n: agente.name || agente.hostname || '—' })}
                  sotto={t('Su richiesta: l’agente lo consegna al prossimo keep-alive, entro un minuto.')} onChiudi={() => setLog(false)}>
          <LogDispositivo kind="agent" id={agente.id} nome={agente.name || agente.hostname || 'agente'} />
        </Finestra>
      )}
      <button type="button" className="link" disabled={busy}
        onClick={() => azione(async () => { onConfig(await api.regenerateInventoryAgent(agente.id)) })}>{t('Rigenera')}</button>
      <button type="button" className="link" disabled={busy} onClick={onToggle}>{aperto ? t('Chiudi') : t('Impostazioni')}</button>
      <button type="button" className="link" disabled={busy}
        onClick={() => {
          if (!agente.revoked && !window.confirm(t('Revocare l’agente «{nome}»? Smette subito di consegnare: ciò che manderà verrà rifiutato finché non lo riattivi.', { nome: agente.name || agente.hostname || '' }))) return
          void azione(() => api.updateInventoryAgent(agente.id, { revoked: !agente.revoked }))
        }}>
        {agente.revoked ? t('Riattiva') : t('Revoca')}</button>
      <button type="button" className="link" disabled={busy}
        onClick={() => {
          if (!window.confirm(t('Eliminare l’agente «{nome}»?\n\nSparisce anche la sua storia: quante consegne ha fatto e quando ha parlato l’ultima volta. Per fermarlo senza perderla, usa Revoca.', { nome: agente.name || agente.hostname || '—' }))) return
          azione(() => api.deleteInventoryAgent(agente.id))
        }}>{t('Elimina')}</button>
    </div>
  )
}

/** Barra azioni massive sugli agenti selezionati: features + manutenzione. */
function AzioniMassiveAgenti({ ids, onFatto, onError }: { ids: string[]; onFatto: () => void; onError: (e: unknown) => void }) {
  const [busy, setBusy] = useState(false)
  async function applica(patch: Record<string, unknown>) {
    setBusy(true)
    onError(null)
    try { await api.bulkInventoryAgents(ids, patch); onFatto() } catch (e) { onError(e) } finally { setBusy(false) }
  }
  const coppia = (chiave: string, label: string) => (
    <span key={chiave} style={{ whiteSpace: 'nowrap' }}>
      <span className="text-muted">{label}</span>{' '}
      <button type="button" className="link" disabled={busy} onClick={() => applica({ [chiave]: true })}>{t('attiva')}</button>
      {' · '}
      <button type="button" className="link" disabled={busy} onClick={() => applica({ [chiave]: false })}>{t('disattiva')}</button>
    </span>
  )
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
      {coppia('sbom', 'SBOM')}
      {coppia('runtime', t('Runtime'))}
      {coppia('app_observe', 'eBPF')}
      {coppia('dhcp_listen', t('Ascolto DHCP'))}
      {coppia('auto_update', t('Aggiornamento automatico'))}
      <span style={{ whiteSpace: 'nowrap' }}>
        <span className="text-muted">{t('Ambito')}</span>{' '}
        <select disabled={busy} defaultValue="" aria-label={t('Ambito di raccolta')}
          onChange={(e) => { if (e.target.value) void applica({ mode: e.target.value }) }}>
          <option value="">—</option>
          <option value="fast">{t('Rapido')}</option>
          <option value="full">{t('Completo')}</option>
          <option value="software">{t('Rapido + software')}</option>
          <option value="all">{t('Completo + software')}</option>
        </select>
      </span>
    </div>
  )
}

/**
 * Cosa fa un agente, cambiabile dopo la creazione.
 *
 * ## Perché non serve reinstallare
 *
 * Le impostazioni arrivano alla macchina con la **risposta alla prossima
 * consegna**: l'agente le scrive nel proprio file e le applica dal giro dopo.
 * Prima si sceglievano una volta sola, e cambiarle voleva dire rigenerare il
 * segreto e reinstallare — cioè andare dove l'agente gira, che sulle macchine
 * per cui l'agente esiste è precisamente la cosa impossibile.
 */
function Impostazioni({
  agente,
  onSaved,
  onError,
  onAnnulla,
}: {
  agente: InventoryAgent
  onSaved: () => void
  /** «Annulla»: closes the settings without saving (the saved values stay). */
  onAnnulla?: () => void
  onError: (err: unknown) => void
}) {
  const config = (agente.config ?? {}) as Record<string, unknown>
  const [modo, setModo] = useState(String(config.mode ?? 'all'))
  const [minuti, setMinuti] = useState(String(config.interval_minutes ?? 1440))
  const [dove, setDove] = useState(String(config.endpoint ?? ''))
  const [auto, setAuto] = useState(config.auto_update !== false)
  const [dhcp, setDhcp] = useState(config.dhcp_listen === true)
  const [sbom, setSbom] = useState(config.sbom === true)
  const [runtime, setRuntime] = useState(config.runtime === true)
  const [osserva, setOsserva] = useState(config.app_observe === true)
  const statoOsserva = (config.app_observe_state ?? null) as null | { enabled?: boolean; running?: boolean; error?: string }
  const statoRuntime = (config.runtime_state ?? null) as null | {
    enabled?: boolean; running?: boolean; address?: string; error?: string
  }
  const [busy, setBusy] = useState(false)
  const destinazioni = useAsync(() => api.inventoryDeliveryTargets(), [])
  // Lo stato dell'ascolto DHCP come l'agente lo riporta a ogni keep-alive.
  // Utilizzo AI: la lettura dei nomi risolti sulla macchina (agente: ai.go).
  const statoAi = (config.ai_state ?? null) as null | {
    catalog?: number; running?: boolean; method?: string; recognized?: number; error?: string
  }
  const statoDhcp = (config.dhcp_listen_state ?? null) as null | {
    enabled?: boolean; running?: boolean; packets?: number; leases?: number
    pending?: number; error?: string | null; lease_error?: string | null; interfaces?: string[]
  }

  async function salva() {
    setBusy(true)
    onError(null)
    try {
      await api.updateInventoryAgent(agente.id, {
        mode: modo as 'fast' | 'full' | 'software' | 'all',
        interval_minutes: Number(minuti),
        endpoint: dove,
        auto_update: auto,
        dhcp_listen: dhcp,
        sbom,
        runtime,
        app_observe: osserva,
      })
      onSaved()
    } catch (err) {
      onError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card__inset">
      <div className="grid grid--2">
        <div className="field">
          <label htmlFor={`m-${agente.id}`}>{t('Quanto raccogliere')}</label>
          <select
            id={`m-${agente.id}`}
            value={modo}
            onChange={(e) => setModo(e.target.value)}
          >
            <option value="fast">{t('Rapido')}</option>
            <option value="full">{t('Completo')}</option>
            <option value="software">{t('Rapido + software')}</option>
            <option value="all">{t('Completo + software')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={`i-${agente.id}`}>{t('Ogni quanti minuti')}</label>
          <input
            id={`i-${agente.id}`}
            type="number"
            min={5}
            max={10080}
            value={minuti}
            onChange={(e) => setMinuti(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`e-${agente.id}`}>{t('Consegna a')}</label>
          <select
            id={`e-${agente.id}`}
            value={dove}
            onChange={(e) => setDove(e.target.value)}
          >
            <option value="">{t('Il Control Plane (cloud)')}</option>
            {(destinazioni.data ?? [])
              .filter((d) => d.value !== '')
              .map((d) => (
                <option key={d.label} value={d.value} disabled={!d.available}>
                  {t(d.label)}
                </option>
              ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`a-${agente.id}`}>{t('Aggiornamento')}</label>
          <label className="check" id={`a-${agente.id}`}>
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
            />{' '}
            {t('Aggiorna l’agente da sé')}
          </label>
        </div>
        <div className="field">
          <label htmlFor={`d-${agente.id}`}>{t('Ascolto DHCP')}</label>
          <label className="check" id={`d-${agente.id}`}>
            <input
              type="checkbox"
              checked={dhcp}
              onChange={(e) => setDhcp(e.target.checked)}
            />{' '}
            {t('Ascolta le richieste DHCP e leggi i lease (solo sul server DHCP di Windows)')}
          </label>
          <p className="field__hint">
            {t('Con gli ip helper sui gateway il server DHCP riceve le richieste di tutte le VLAN: l’agente ne legge MAC, nome, impronta del sistema operativo e VLAN di provenienza, più i lease del servizio, e li consegna col keep-alive come l’ascolto passivo della sonda.')}
          </p>
          {statoDhcp && (
            <p className="field__hint">
              {statoDhcp.running
                ? t('In ascolto su {n} indirizzi: {p} richieste sentite, {l} lease letti, {q} da consegnare.')
                    .replace('{n}', String(statoDhcp.interfaces?.length ?? 0))
                    .replace('{p}', String(statoDhcp.packets ?? 0))
                    .replace('{l}', String(statoDhcp.leases ?? 0))
                    .replace('{q}', String(statoDhcp.pending ?? 0))
                : statoDhcp.enabled
                  ? t('Acceso ma non in ascolto: l’agente non ha ancora aperto le prese o non può (serve Windows e i diritti di amministratore).')
                  : t('Spento.')}
              {statoDhcp.error ? ` ${t('Errore')}: ${statoDhcp.error}` : ''}
              {statoDhcp.lease_error ? ` ${t('Lease')}: ${statoDhcp.lease_error}` : ''}
            </p>
          )}
        </div>
        {statoAi && (statoAi.catalog ?? 0) > 0 && (
          <div className="field">
            <span className="field__label">{t('Utilizzo AI')}</span>
            <p className="field__hint">
              {statoAi.error
                ? `${t('Lettura dei nomi non attiva')}: ${statoAi.error}`
                : statoAi.running
                  ? t('Legge i nomi che la macchina risolve ({m}): {n} servizi nel catalogo, {r} usi riconosciuti dall’avvio.', {
                    m: statoAi.method === 'dns_cache' ? t('cache DNS di Windows') : statoAi.method === 'dns_listen' ? t('risposte DNS') : (statoAi.method ?? ''),
                    n: statoAi.catalog ?? 0, r: statoAi.recognized ?? 0,
                  })
                  : t('Catalogo ricevuto, lettura in avvio.')}
            </p>
          </div>
        )}
        <div className="field">
          <label htmlFor={`s-${agente.id}`}>{t('SBOM')}</label>
          <label className="check" id={`s-${agente.id}`}>
            <input
              type="checkbox"
              checked={sbom}
              onChange={(e) => setSbom(e.target.checked)}
            />{' '}
            {t('Leggi il software installato e le sue librerie (CENSO SBOM)')}
          </label>
          <p className="field__hint">
            {t('L’agente legge tutto il software installato — cartelle di installazione (Program Files e le cartelle in radice dei dischi, /opt, /srv, /usr/local, /var/www), processi in esecuzione, servizi anche fermi, siti IIS, librerie globali di pip e npm, più i percorsi personalizzati — e ne legge i componenti (Java, Node.js, Python, .NET, PHP, Ruby, Go) a bassa priorità e con un tempo massimo. Le porte in ascolto servono a legare le applicazioni a chiamate e traffico. Il risultato va nella Mappa software e nella sezione SBOM del CI. Richiede la licenza CENSO SBOM.')}
          </p>
        </div>
        <div className="field">
          <label htmlFor={`r-${agente.id}`}>{t('Runtime')}</label>
          <label className="check" id={`r-${agente.id}`}>
            <input type="checkbox" checked={runtime} onChange={(e) => setRuntime(e.target.checked)} />{' '}
            {t('Ricevi le tracce OpenTelemetry delle applicazioni (CENSO SBOM)')}
          </label>
          <p className="field__hint">
            {t('L’agente apre un ricevitore OTLP/HTTP solo su 127.0.0.1:4318, la porta predefinita degli SDK e del Collector: nelle applicazioni basta OTEL_EXPORTER_OTLP_ENDPOINT=http://127.0.0.1:4318 (protocollo http/protobuf o http/json); un OpenTelemetry Collector già presente sulla macchina vi esporta con otlphttp. Le tracce restano sul server: ogni cinque minuti l’agente manda solo operazioni, chiamate e legami fra servizi con conteggi, errori e tempi, senza query, SQL, header o contenuti. Serve l’agente residente; gRPC non è ancora supportato.')}
          </p>
          {statoRuntime && (
            <p className="field__hint">
              {statoRuntime.running
                ? t('In ascolto su {a}.', { a: statoRuntime.address ?? '127.0.0.1:4318' })
                : statoRuntime.enabled
                  ? t('Acceso ma non in ascolto.')
                  : t('Spento.')}
              {statoRuntime.error ? ` ${t('Errore')}: ${statoRuntime.error}` : ''}
            </p>
          )}
        </div>
        <div className="field">
          <label htmlFor={`o-${agente.id}`}>{t('Osservazione applicativa')}</label>
          <label className="check" id={`o-${agente.id}`}>
            <input type="checkbox" checked={osserva} onChange={(e) => setOsserva(e.target.checked)} />{' '}
            {t('Osserva le richieste HTTP di tutte le applicazioni con eBPF, senza toccarle (Linux, CENSO SBOM)')}
          </label>
          <p className="field__hint">
            {t('Un programma eBPF guarda le chiamate di sistema e riconosce le richieste e le risposte HTTP di ogni processo, lato server e lato client: metodo, percorso, codice di stato e tempo di risposta, senza agenti nelle applicazioni né riavvii. Escono dal kernel solo la riga della richiesta e il codice di stato, mai header o contenuti; i parametri della richiesta si scartano. Il risultato va nella sezione Runtime del CI e nella Mappa Software, insieme a OpenTelemetry. Serve Linux 5.8 o successivo con BTF e l’agente residente come root; l’HTTPS è cifrato a quel livello e non si vede ancora.')}
          </p>
          {statoOsserva && (
            <p className="field__hint">
              {statoOsserva.running ? t('Attiva.') : statoOsserva.enabled ? t('Accesa ma non attiva.') : t('Spenta.')}
              {statoOsserva.error ? ` ${t('Errore')}: ${statoOsserva.error}` : ''}
            </p>
          )}
        </div>
      </div>
      <p className="muted">
        {t("Arrivano alla macchina con la prossima consegna: nessuno deve tornarci sopra, e il segreto non cambia — rigenerarlo per cambiare un'impostazione invaliderebbe l'agente che sta funzionando.")}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={salva} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
        {onAnnulla && (
          <button type="button" className="secondary" onClick={onAnnulla} disabled={busy}>{t('Annulla')}</button>
        )}
      </div>
    </div>
  )
}

function NuovoAgente({
  onCancel,
  onDone,
}: {
  onCancel: () => void
  onDone: (agente: InventoryAgent) => void
}) {
  const [name, setName] = useState('')
  const [chiave, setChiave] = useState(false)
  const [description, setDescription] = useState('')
  const [ttlDays, setTtlDays] = useState('365')
  const [intervalMinutes, setIntervalMinutes] = useState('1440')
  const [endpoint, setEndpoint] = useState('')
  // `full` è il predefinito: il senso di un agente d'inventario è sapere cosa
  // c'è su quella macchina, e chi ha bisogno di meno lo dice. Il contrario —
  // partire dal minimo — produce parchi inventariati a metà che sembrano
  // completi.
  const [modo, setModo] = useState<'fast' | 'full' | 'software' | 'all'>('all')
  // Acceso di default: un parco che si aggiorna solo quando qualcuno passa a
  // mano resta indietro, e la versione più vecchia è quella che gira sulle
  // macchine che nessuno guarda.
  const [autoUpdate, setAutoUpdate] = useState(true)
  const destinazioni = useAsync(() => api.inventoryDeliveryTargets(), [])
  const scelta = (destinazioni.data ?? []).find((d) => d.value === endpoint)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function crea() {
    setBusy(true)
    setError(null)
    try {
      onDone(
        await api.createInventoryAgent({
          name: name.trim(),
          installation_key: chiave,
          description: description.trim() || null,
          ttl_days: Number(ttlDays),
          interval_minutes: Number(intervalMinutes),
          endpoint: endpoint.trim() || null,
          mode: modo,
          auto_update: autoUpdate,
        }),
      )
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo agente')}>
      <ErrorBanner error={error} />
      <div className="row">
        <div className="field">
          <label htmlFor="ag-name">{t('Nome')}</label>
          <input
            id="ag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Server ERP in cloud')}
          />
          <p className="field__hint">
            {chiave
              ? t('Il nome della chiave (es. «Server Milano»): ogni macchina compare poi in elenco con il proprio nome.')
              : t('Facoltativo: vuoto, l’agente prende il nome della macchina quando si installa e si annuncia.')}
          </p>
          <label className="checkbox">
            <input type="checkbox" checked={chiave} onChange={(e) => setChiave(e.target.checked)} />{' '}
            {t('Chiave d’installazione per più macchine')}
          </label>
          <p className="field__hint">
            {t('La stessa configurazione (o lo stesso pacchetto) si installa su tutte le macchine, anche con GPO, SCCM o Ansible: al primo avvio ognuna si registra come agente a sé, con il nome della macchina, le impostazioni e i tag della chiave, e un segreto suo generato sulla macchina. Revocare la chiave ferma le nuove registrazioni, non gli agenti già registrati.')}
          </p>
        </div>
        <div className="field" style={{ flex: 2 }}>
          <label htmlFor="ag-desc">{t('Descrizione')}</label>
          <input
            id="ag-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('A quale macchina corrisponde, per chi lo troverà fra sei mesi')}
          />
        </div>
      </div>

      <div className="row">
        <div className="field" style={{ flex: '0 1 180px' }}>
          <label htmlFor="ag-ttl">{t('Validità del segreto (giorni)')}</label>
          <input
            id="ag-ttl"
            type="number"
            min={0}
            max={3650}
            value={ttlDays}
            onChange={(e) => setTtlDays(e.target.value)}
          />
          <p className="field__hint">
            {/* La scadenza non è prudenza generica: il file resta sulla macchina
                anche quando la macchina viene venduta. */}
            {t('0 = senza scadenza. Con la scadenza il caso peggiore è un inventario che manca — e si vede — invece di un permesso che sopravvive alla macchina.')}
          </p>
        </div>
        <div className="field" style={{ flex: '0 1 180px' }}>
          <label htmlFor="ag-interval">{t('Ogni quanto consegna (minuti)')}</label>
          <input
            id="ag-interval"
            type="number"
            min={5}
            max={10080}
            value={intervalMinutes}
            onChange={(e) => setIntervalMinutes(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ag-mode">{t('Quanto raccogliere')}</label>
          <select
            id="ag-mode"
            value={modo}
            onChange={(e) =>
              setModo(e.target.value as 'fast' | 'full' | 'software' | 'all')
            }
          >
            <option value="fast">{t('Rapido — identità, sistema, rete')}</option>
            <option value="full">
              {t('Completo — dischi, servizi, account, sicurezza')}
            </option>
            <option value="software">{t('Rapido + software installato')}</option>
            <option value="all">{t('Completo + software installato')}</option>
          </select>
          <p className="field__hint">
            {modo === 'fast'
              ? t('Un secondo di lavoro. Le sezioni non raccolte vengono dichiarate: un inventario magro non si legge come una macchina povera.')
              : modo === 'software'
                ? t('L’elenco dei prodotti interroga il gestore di pacchetti o il registro: secondi, non millisecondi.')
                : t('Lo stesso documento che la sonda produce entrando via SSH o WinRM, così i due inventari della stessa macchina si riconciliano invece di affiancarsi.')}
          </p>
        </div>
        <div className="field">
          <label htmlFor="ag-auto">{t('Aggiornamento')}</label>
          <label className="check" id="ag-auto">
            <input
              type="checkbox"
              checked={autoUpdate}
              onChange={(e) => setAutoUpdate(e.target.checked)}
            />{' '}
            {t('Aggiorna l’agente da sé')}
          </label>
          <p className="field__hint">
            {t('Dopo una consegna riuscita, se il Control Plane distribuisce una versione più recente. Il binario scaricato viene eseguito prima di essere installato, e il precedente resta accanto: un aggiornamento difettoso non spegne la macchina. Toglilo dove esistono finestre di manutenzione.')}
          </p>
        </div>
        <div className="field">
          <label htmlFor="ag-endpoint">{t('Consegna a')}</label>
          <select
            id="ag-endpoint"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
          >
            {/* Il cloud c'è sempre, anche mentre l'elenco si carica: il caso
                normale non deve aspettare una richiesta per essere scelto. */}
            <option value="">{t('Il Control Plane (cloud)')}</option>
            {(destinazioni.data ?? [])
              .filter((d) => d.value !== '')
              .map((d) => (
                <option key={d.label} value={d.value} disabled={!d.available}>
                  {d.available ? t(d.label) : t('{nome} (non disponibile)', { nome: t(d.label) })}
                </option>
              ))}
          </select>
          <p className="field__hint">
            {scelta?.hint
              ? t(scelta.hint)
              : t('Una sonda serve solo se la macchina non raggiunge il Control Plane ma raggiunge lei. Chi inoltra non legge: il carico è sigillato.')}
          </p>
          {/* Le due cose che chi sceglie una sonda deve sapere, e che un
              indirizzo da solo non dice. */}
          {endpoint !== '' && (
            <p className="field__hint">
              {t("Se quella sonda non risponde, l'agente consegna al Control Plane e lo dichiara nel proprio log: l'inventario non si ferma, e la sonda da riparare resta visibile.")}
            </p>
          )}
          {(destinazioni.data ?? []).some((d) => !d.available) && (
            <p className="field__hint">
              {t("Le sonde marcate «non disponibile» non hanno il ricevitore acceso: si accende dalla scheda della sonda, sezione «Consegne degli agenti d'inventario». È spento di default perché è l'unica porta che una sonda apre in ingresso.")}
            </p>
          )}
        </div>
      </div>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={crea} disabled={busy || (chiave && !name.trim())}>
          {busy ? t('Creazione…') : t('Crea e genera la configurazione')}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          {t('Annulla')}
        </button>
      </div>
    </Card>
  )
}

/**
 * Come si installa l'agente su una macchina.
 *
 * ## Un comando, come per la sonda
 *
 * Prima erano quattro passaggi da copiare a mano — crea la cartella, incolla la
 * configurazione, scarica il binario, mettilo a orario — su una macchina che
 * **per definizione** è quella che nessuno raggiunge: se uno va storto, non si
 * torna a guardare.
 *
 * Il file resta disponibile, ma in secondo piano: serve dove non si scarica
 * niente, cioè dove il comando non funzionerebbe comunque.
 *
 * ## Perché la scadenza è in evidenza
 *
 * Perché il comando contiene un URL che vale un'ora, e chi lo copia per
 * incollarlo domani troverebbe un 404 senza sapere perché. La riga lo dice
 * prima, non dopo.
 */
function Installazione({
  agente,
  onClose,
}: {
  agente: InventoryAgent
  onClose: () => void
}) {
  const [copiato, setCopiato] = useState<string | null>(null)
  const [mostraFile, setMostraFile] = useState(false)
  const file = agente.config_file

  // Riferimento dei flag dell'eseguibile (gli stessi su Windows/Linux/macOS):
  // di norma non servono — il comando qui sopra li imposta già — ma chi installa
  // con GPO/SCCM/Ansible o a mano li cerca, e cercarli nel sorgente non è un'opzione.
  const flagCli: Array<[string, string]> = [
    ['-version', t('Stampa la versione ed esce.')],
    ['-config <file>', t('File di configurazione da usare (predefinito: accanto all’eseguibile).')],
    ['-mode fast|full|software|all', t('Quanto raccogliere (vuoto = quello scelto qui).')],
    ['-service', t('Resta in esecuzione: keep-alive ogni minuto, inventario all’intervallo scelto.')],
    ['-no-watchdog', t('Non registra il sorvegliante (su Windows l’attività pianificata): lo avvia un supervisore esterno. Come la variabile CENSO_NO_WATCHDOG.')],
    ['-dry-run', t('Raccoglie e mostra l’inventario senza sigillarlo né consegnarlo.')],
    ['-out <file>', t('Scrive la busta sigillata su file invece di consegnarla (per reti senza uscita).')],
    ['-timeout <durata>', t('Quanto attendere la consegna (predefinito 30 secondi).')],
    ['-v', t('Elenca a video ogni dato non letto.')],
    ['install', t('L’agente si installa da solo (accetta -config, -force, -no-watchdog).')],
    ['uninstall', t('Disinstalla l’agente (lo usa anche l’MSI di Windows in rimozione).')],
  ]

  function copia(cosa: string, testo: string) {
    navigator.clipboard?.writeText(testo)
    setCopiato(cosa)
  }

  return (
    <Card title={t('Installa «{nome}»', { nome: agente.name || t('nuovo agente') })}>
      {/* The yellow note says what «Rigenera» really does. For a key it is
          not «the agent stops delivering»: the old command and package stop
          enrolling new machines, the enrolled ones keep their own secret. */}
      <div className="banner banner--warn">
        {agente.is_key
          ? (agente.install_expires_at
            ? t('Il comando vale un’ora (fino alle {ora}) e contiene il segreto di questa chiave: è l’unica volta in cui compare. Passata la finestra si rigenera — rigenerare la chiave invalida il comando e il pacchetto vecchi (le macchine nuove non si registrano più con quelli), ma le macchine già registrate continuano a funzionare: ognuna ha un segreto suo.', { ora: formatDateTime(agente.install_expires_at) })
            : t('Il comando vale un’ora e contiene il segreto di questa chiave: è l’unica volta in cui compare. Passata la finestra si rigenera — rigenerare la chiave invalida il comando e il pacchetto vecchi (le macchine nuove non si registrano più con quelli), ma le macchine già registrate continuano a funzionare: ognuna ha un segreto suo.'))
          : agente.install_expires_at
            ? t('Il comando vale un’ora (fino alle {ora}), e contiene il segreto di questo agente: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’agente era già installato smette di consegnare finché non lo reinstalli.', {
                ora: formatDateTime(agente.install_expires_at),
              })
            : t('Il comando vale un’ora, e contiene il segreto di questo agente: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’agente era già installato smette di consegnare finché non lo reinstalli.')}
      </div>

      {agente.is_key && (
        <div className="banner banner--info">
          {t('Questa è una chiave d’installazione: lo stesso comando o lo stesso pacchetto vale per tutte le macchine. Ognuna, al primo avvio, si registra come agente a sé con il nome della macchina e un segreto generato lì; rigenerare la chiave non tocca le macchine già registrate. Serve l’agente nella versione corrente.')}
        </div>
      )}

      {agente.install_via && (
        <div className="banner banner--info">
          {t('Si installa dalla sonda «{sonda}» ({indirizzo}), in HTTPS con il certificato della sonda verificato: la macchina non deve raggiungere il Control Plane.', {
            sonda: agente.install_via.probe, indirizzo: agente.install_via.base,
          })}
        </div>
      )}
      {agente.install_note && <div className="banner banner--warn">{agente.install_note}</div>}

      <div className="field">
        <label>{t('Su Linux o macOS')}</label>
        <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
          {agente.install_command}
        </pre>
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button onClick={() => copia('unix', agente.install_command ?? '')}>
            {copiato === 'unix' ? t('Copiato') : t('Copia il comando')}
          </button>
        </div>
        <p className="field__hint">
          {/* Il passo che di solito si salta è quello che dice se l'agente ha i
              privilegi giusti: senza root mancano seriale e modello, e l'agente
              lo scrive invece di lasciarlo sembrare assente. */}
          {t('Scarica il binario, scrive la configurazione, prova a raccogliere e mette l’esecuzione periodica. Quello che stampa dice anche cosa non è riuscito a leggere: senza privilegi mancano numero di serie e modello.')}
        </p>
      </div>

      {/* Le due righe vanno incollate **insieme**: la prima scarica, la seconda
          esegue. Chi ne incolla una sola non ottiene un errore — ottiene metà
          lavoro, che è peggio. */}

      <div className="field">
        <label>{t('Su Windows (PowerShell come amministratore)')}</label>
        {/* Un blocco di righe e non una sola: con il certificato autofirmato del
            primo avvio il comando ne appunta l'impronta prima di scaricare, e
            `white-space: pre` è ciò che impedisce a un a capo di sparire nel
            copia-e-incolla — una riga persa qui produce uno script che fallisce
            a metà. */}
        <pre
          className="mono"
          style={{ overflowX: 'auto', padding: 12, whiteSpace: 'pre' }}
        >
          {agente.install_command_windows}
        </pre>
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button
            className="secondary"
            onClick={() => copia('win', agente.install_command_windows ?? '')}
          >
            {copiato === 'win' ? t('Copiato') : t('Copia il comando')}
          </button>
        </div>
        <VarianteSenzaCurl comando={agente.install_command_windows_legacy} />
        <p className="field__hint">
          {t('Se un EDR (per esempio Cynet) blocca il comando, usa il pacchetto Windows qui sotto anche per una macchina sola: si estrae lo zip e si lancia install.cmd da amministratore. Non c’è nessuno script PowerShell e nessuno scaricamento, perché l’agente si installa da solo.')}
        </p>
      </div>

      {agente.packages && (
        <PacchettoDistribuzione pacchetti={agente.packages} destinazioneAgente={agente.delivery_endpoint ?? ''} />
      )}

      {agente.rpm && Object.keys(agente.rpm).length > 0 && (
        <div className="field">
          <label>{t('Pacchetto RPM (Linux: RHEL, Rocky, AlmaLinux, Oracle, SUSE…)')}</label>
          <p style={{ marginTop: 0 }}>
            {Object.entries(agente.rpm).map(([arch, url]) => (
              <a key={arch} href={url} style={{ marginRight: 16 }} download>
                {`censo-inventory-linux-${arch}.rpm`}
              </a>
            ))}
          </p>
          {/* Istruzioni sempre visibili: l'RPM è generico (binario + unit
              systemd, nessuna configurazione), e chi lo installa deve sapere che
              la configurazione la mette a parte e che il servizio lo avvia la
              sua piattaforma — non un cron. */}
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('L’RPM contiene il binario e il servizio systemd «censo-inventory», ma non la configurazione: lo stesso pacchetto va su molti host, mentre il file con il segreto è diverso per ciascuno. Lo installa la tua piattaforma (Ansible) e lo avvia systemd, non il cron.')}
          </p>
          <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
{`# 1. installa il pacchetto (su ogni host)
sudo dnf install ./censo-inventory-linux-amd64.rpm      # oppure -arm64

# 2. metti la configurazione di QUESTO host (contiene il suo segreto)
sudo install -o root -g root -m 600 inventory.json /etc/censo/inventory.json

# 3. avvia e abilita al boot (lo fa la tua piattaforma; a mano:)
sudo systemctl enable --now censo-inventory`}
          </pre>
          <p className="field__hint">
            {t('Da Ansible: ansible.builtin.dnf per il pacchetto, un template per /etc/censo/inventory.json (0600), ansible.builtin.systemd con enabled e state=started. La configurazione di questo host è il file qui sotto (oppure rigenera il comando per averne una nuova).')}
          </p>
          {agente.rpm_gpg_key && (
            <div style={{ marginTop: 12 }}>
              <label>{t('Firma del pacchetto (GPG)')}</label>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('Gli RPM sono firmati: importa la chiave pubblica e verifica la firma prima di installare. Per un repository, metti la chiave e gpgcheck=1.')}{' '}
                <a href={agente.rpm_gpg_key} download>{t('Scarica la chiave pubblica')}</a>
              </p>
              <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
{`# importa la chiave pubblica di firma
sudo rpm --import ${agente.rpm_gpg_key}

# verifica la firma del pacchetto (deve dire: digests signatures OK)
rpm --checksig censo-inventory-linux-amd64.rpm`}
              </pre>
            </div>
          )}
          {!agente.rpm_gpg_key && (
            <p className="field__hint" style={{ marginTop: 12 }}>
              {t('Gli RPM di questa installazione non sono firmati: un dnf o yum con gpgcheck=1 li rifiuta («is not signed»). La firma si configura sul server con la chiave privata di firma (docs/operations/firma-pacchetti-rpm.md); finché manca, si installano con «dnf install --nogpgcheck» dopo averne controllato la provenienza.')}
            </p>
          )}
        </div>
      )}

      {agente.downloads && (
        <div className="field">
          <label>{t('Oppure scarica il binario e configura a mano')}</label>
          {/* Per chi non può eseguire lo script — policy sugli script scaricati,
              macchine senza uscita verso la console — o vuole solo il file. I
              collegamenti valgono quanto il comando: portano il segreto. */}
          <p style={{ marginTop: 0 }}>
            {Object.entries(agente.downloads).map(([piattaforma, url]) => (
              <a key={piattaforma} href={url} style={{ marginRight: 16 }} download>
                {piattaforma.startsWith('windows')
                  ? `${piattaforma} (.exe)`
                  : piattaforma}
              </a>
            ))}
          </p>
          <p className="field__hint">
            {t('Il file scaricato va accompagnato dal file di configurazione: senza, l’agente non sa a chi consegnare. Lo script lo scrive da solo; a mano, chiedi la configurazione rigenerando il comando.')}
          </p>
        </div>
      )}

      {/* Il file a mano resta, in secondo piano: serve dove la macchina non
          raggiunge il Control Plane nemmeno per scaricare — cioè dove il
          comando non funzionerebbe comunque. */}
      {file && (
        <details style={{ marginTop: 12 }} open={mostraFile}>
          <summary
            style={{ cursor: 'pointer' }}
            onClick={() => setMostraFile(!mostraFile)}
          >
            {t('La macchina non raggiunge il Control Plane: metto il file a mano')}
          </summary>
          <p className="text-muted" style={{ marginTop: 8 }}>
            {t('Copia il binario censo-inventory sulla macchina, e questo file in /etc/censo/inventory.json con permessi 600. Se non c’è rete in uscita affatto, l’agente scrive la busta su file con -out: è sigillata, quindi può viaggiare su una chiavetta.')}
          </p>
          <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
            {JSON.stringify(file, null, 2)}
          </pre>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button
              className="secondary"
              onClick={() => copia('file', JSON.stringify(file, null, 2))}
            >
              {copiato === 'file' ? t('Copiato') : t('Copia il file')}
            </button>
          </div>
        </details>
      )}

      {/* Riferimento dei flag: a scomparsa perché di norma il comando qui sopra
          li imposta già; serve a chi installa con GPO/SCCM/Ansible o a mano. */}
      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer' }}>
          {t('Opzioni della riga di comando (censo-inventory)')}
        </summary>
        <p className="text-muted" style={{ marginTop: 8 }}>
          {t('Gli stessi flag su Windows, Linux e macOS. Di norma non servono — il comando qui sopra li imposta già.')}
        </p>
        <dl className="flag-ref" style={{ margin: 0 }}>
          {flagCli.map(([flag, desc]) => (
            <div key={flag} style={{ display: 'flex', gap: 12, padding: '4px 0', flexWrap: 'wrap' }}>
              <dt className="mono" style={{ minWidth: 220, fontWeight: 600 }}>{flag}</dt>
              <dd style={{ margin: 0, flex: 1 }}>{desc}</dd>
            </div>
          ))}
        </dl>
      </details>

      <div className="rowactions" style={{ textAlign: 'left', marginTop: 16 }}>
        <button className="secondary" onClick={onClose}>
          {t('Ho finito, chiudi')}
        </button>
      </div>
    </Card>
  )
}

/**
 * L'ultimo contatto dell'agente residente (ADR-0022 §5ter): si fa sentire ogni
 * minuto, quindi oltre 3 minuti di silenzio è spento o scollegato. Un agente
 * che non ha mai mandato un keep-alive è della versione precedente (attività
 * periodica): lo si dice, perché si converte da solo con l'aggiornamento.
 */
function Contatto({ agente }: { agente: InventoryAgent }) {
  if (!agente.last_keepalive_at) {
    return (
      <span className="text-muted" title={t('Questo agente non è ancora residente: consegna solo al suo intervallo. Con l’aggiornamento automatico diventa residente da solo.')}>
        {t('non residente')}
      </span>
    )
  }
  const secondi = (Date.now() - new Date(agente.last_keepalive_at).getTime()) / 1000
  const vivo = secondi < 180
  return (
    <>
      <span className={`badge badge--${vivo ? 'ok' : 'warn'}`}>{vivo ? t('in linea') : t('silenzioso')}</span>{' '}
      <span className="text-muted">{timeAgo(agente.last_keepalive_at)}</span>
      {agente.agent_version && (
        <>
          <br />
          <span className="text-muted mono">{agente.agent_version}</span>
        </>
      )}
      {agente.next_inventory_at && (
        <>
          <br />
          <span className="text-muted">{t('prossimo inventario {quando}', { quando: formatDateTime(agente.next_inventory_at) })}</span>
        </>
      )}
    </>
  )
}
